import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { accountStorage, scopeToAccount } from '../lib/accountStorage';

// A single global, editable kanban flow (process columns) used everywhere the
// Tasks kanban renders. Persisted to localStorage for now; cloud sync can be
// layered on later (it's small per-user config). Tasks reference a column by id
// via `task.kanbanStatus`.
//
// ⚠️ One flow per ACCOUNT (lib/accountStorage): it was one key for the device, so the next
// account to sign in got the last one's column names. Kept, not cleared, on a sign-out —
// this device is the only copy.

export interface KanbanColumn {
  id: string;
  name: string;
  color: string;
  order: number;
  isDone?: boolean; // moving a task into this column marks it completed
}

export const DEFAULT_KANBAN_COLUMNS: KanbanColumn[] = [
  { id: 'backlog', name: 'Backlog', color: '#94a3b8', order: 0 },
  { id: 'todo', name: 'To Do', color: '#3b82f6', order: 1 },
  { id: 'in-progress', name: 'In Progress', color: '#f59e0b', order: 2 },
  { id: 'review', name: 'Review', color: '#8b5cf6', order: 3 },
  { id: 'completed', name: 'Completed', color: '#22c55e', order: 4, isDone: true },
];

function sortByOrder(cols: KanbanColumn[]): KanbanColumn[] {
  return [...cols].sort((a, b) => a.order - b.order);
}

function slugId(name: string): string {
  const base = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'column';
  return `${base}-${Math.floor(performance.now() % 100000)}`;
}

interface KanbanState {
  columns: KanbanColumn[];
  addColumn: (name: string, color: string) => string; // returns new id
  updateColumn: (id: string, updates: Partial<Omit<KanbanColumn, 'id'>>) => void;
  removeColumn: (id: string) => void;
  reorderColumns: (sourceIndex: number, destinationIndex: number) => void;
  resetColumns: () => void;
  getColumn: (id: string | undefined) => KanbanColumn | undefined;
  getDoneColumnId: () => string;
  getFirstColumnId: () => string;
  // Map an arbitrary/legacy/undefined status onto a valid current column id.
  normalizeStatus: (status?: string, completed?: boolean) => string;
}

export const useKanbanStore = create<KanbanState>()(
  persist<KanbanState>(
    (set, get) => ({
      columns: DEFAULT_KANBAN_COLUMNS,

      addColumn: (name, color) => {
        const id = slugId(name);
        set((state) => ({
          columns: sortByOrder([
            ...state.columns,
            { id, name: name.trim() || 'Column', color, order: state.columns.length },
          ]),
        }));
        return id;
      },

      updateColumn: (id, updates) => {
        set((state) => ({
          columns: sortByOrder(
            state.columns.map((c) => (c.id === id ? { ...c, ...updates } : c)),
          ),
        }));
      },

      removeColumn: (id) => {
        // Note: callers should reassign tasks out of this column first (the store
        // holds no task state). Keeps at least one column.
        set((state) => {
          if (state.columns.length <= 1) return state;
          const remaining = state.columns
            .filter((c) => c.id !== id)
            .map((c, i) => ({ ...c, order: i }));
          return { columns: remaining };
        });
      },

      reorderColumns: (sourceIndex, destinationIndex) => {
        set((state) => {
          const cols = sortByOrder(state.columns);
          if (
            sourceIndex < 0 || sourceIndex >= cols.length ||
            destinationIndex < 0 || destinationIndex >= cols.length
          ) {
            return state;
          }
          const [moved] = cols.splice(sourceIndex, 1);
          cols.splice(destinationIndex, 0, moved);
          return { columns: cols.map((c, i) => ({ ...c, order: i })) };
        });
      },

      resetColumns: () => set({ columns: DEFAULT_KANBAN_COLUMNS }),

      getColumn: (id) => get().columns.find((c) => c.id === id),

      getDoneColumnId: () => {
        const cols = sortByOrder(get().columns);
        const done = cols.find((c) => c.isDone);
        return (done ?? cols[cols.length - 1]).id;
      },

      getFirstColumnId: () => sortByOrder(get().columns)[0].id,

      normalizeStatus: (status, completed) => {
        const cols = sortByOrder(get().columns);
        if (completed) {
          const done = cols.find((c) => c.isDone) ?? cols[cols.length - 1];
          return done.id;
        }
        if (status && cols.some((c) => c.id === status)) return status;
        return cols[0].id;
      },
    }),
    {
      name: 'kanban-flow-storage',
      version: 1,
      storage: accountStorage('claim'),
    },
  ),
);

scopeToAccount(useKanbanStore);
